// @ts-strict-ignore
import ProductGrid from './ProductGrid'
import { getShouldFetchOnlyMemberDiscountProducts } from './helpers/get-should-fetch-only-member-discount-products'
import { parseFilters } from './helpers/parse-filters'
import { parseFixedFilters } from './helpers/parse-fixed-filters'
import AppointmentUSPBlock from '@components/AppointmentUSPBlock'
import CategoryButtons from '@components/CategoryButtons'
import CtaBanner from '@components/ContentBlocks/CtaBanner'
import InfoBlockSlider from '@components/InfoBlockSlider'
import OverviewHeader from '@components/OverviewHeader'
import { getUrlSlugInLocale } from '@lib/get-url-slug-in-locale'
import { initializeContactForm } from '@lib/initialize-contact-form'
import { SingleItemResponse } from '@lib/kontent'
import { BrandPage } from '@lib/kontent/models/BrandPage'
import { CategoryPage } from '@lib/kontent/models/CategoryPage'
import { ExclusiveCategoryPage } from '@lib/kontent/models/ExclusiveCategoryPage'
import { ThemePage } from '@lib/kontent/models/ThemePage'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import * as qs from 'query-string'
import React, { FC, useEffect } from 'react'
import { useEmarsysTracking } from '@lib/emarsys-tracking/use-emarsys-tracking'
import { useEmarsysScriptIsLoaded, useEmarsysScarabQueueIsLoaded } from '@lib/use-emarsys-store'

type ProductOverviewProps = SingleItemResponse<
  CategoryPage | BrandPage | ThemePage | ExclusiveCategoryPage
> & {
  preProductBanner?: React.ReactNode
}

const promotionLabels = {
  blackfriday2020: 'Black Friday',
}

export const ProductOverview: FC<ProductOverviewProps> = ({
  item,
  modular_content,
  preProductBanner,
}) => {
  const theme =
    item.elements.daf_filters__theme.value.length > 0 && item.elements.daf_filters__theme.value
  const {
    t,
    i18n: { language },
  } = useTranslation('other')
  const router = useRouter()
  const { isTrackingReady, setCategoryVisit, setDefaultSettings, go } = useEmarsysTracking()
  const isPreOwned = (item.elements as CategoryPage)?.category?.value[0]?.codename === 'pre_owned'
  const query = router.asPath.includes('?') ? qs.parse(router.asPath.split('?').pop()) : {}
  const initialPage = parseInt(query['page'] as string, 10) || 1
  const scriptIsLoaded = useEmarsysScriptIsLoaded()
  const scarabQueueIsLoaded = useEmarsysScarabQueueIsLoaded()

  let initialSort = (query['sort'] as string) || 'new'
  // If a sort is set in the CMS, use that as the initial sort
  if (item.elements.daf_filters__sort?.value?.length) {
    initialSort = item.elements.daf_filters__sort.value[0].name
  }

  const fixedFilters = parseFixedFilters(item.elements.daf_filters__attributes.value)
  const initialFilters = query ? { ...parseFilters(query), ...fixedFilters } : {}
  const onlyMemberDiscountProducts = getShouldFetchOnlyMemberDiscountProducts(
    item.elements as ExclusiveCategoryPage,
  )
  const contentSlides = item.elements.content_slides.value.map(
    (key) => modular_content[key].elements,
  )

  useEffect(() => {
    if (scriptIsLoaded && scarabQueueIsLoaded && isTrackingReady) {
      setDefaultSettings()
      setCategoryVisit(item.elements.emarsys_category.value)
      go(['category'])
    }
  }, [item, scriptIsLoaded, scarabQueueIsLoaded, isTrackingReady]) // eslint-disable-line

  let subcategories = []
  if (['brand_page', 'category_page'].includes(item.system.type)) {
    // @ts-ignore
    subcategories = item.elements.subcategory_items.value.map(
      (key) => modular_content[key].elements,
    )
  }
  const hasSubcategories = subcategories.length > 3

  const callToActions = item.elements.product_grid_call_to_actions__items.modular_content.map(
    (key) => ({
      index: modular_content[key].elements.position.value,
      item: modular_content[modular_content[key].elements.item.value[0]].elements,
    }),
  )

  let breadcrumbs = [
    {
      href: `/${item.elements.url_slug}`,
      label: item.elements.title.value,
    },
  ]
  // If category is watch subcategory, add "Watches" to breadcrumbs
  if (
    item.elements.daf_filters__article_type_id.value[0] === '1' &&
    item.system.codename !== 'horloges' &&
    item.system.type === 'category_page'
  ) {
    breadcrumbs.unshift({
      href: `/${getUrlSlugInLocale('watches', language)}`,
      label: t('watches'),
    })
  }

  let collections = []
  let fixedCollections = []
  if (item.elements.daf_filters__collection_nr.value.length) {
    fixedCollections = item.elements.daf_filters__collection_nr.value
      .split(',')
      .map((i) => parseInt(i, 10))
  }
  if (query.collections) {
    collections = (query.collections as string).split(',').map((i) => parseInt(i, 10))
  }

  let brands = []
  let fixedBrands = []
  if (item.elements.daf_filters__brand_nr.value.length) {
    fixedBrands = item.elements.daf_filters__brand_nr.value.split(',').map((i) => parseInt(i, 10))
  }
  if (query.brands) {
    brands = (query.brands as string).split(',').map((i) => parseInt(i, 10))
  }

  let dafPreferenceNumber = null
  if (item.system.type === 'brand_page') {
    const brandElements = item.elements as BrandPage
    dafPreferenceNumber = brandElements.daf_preference_number
      ? brandElements.daf_preference_number.value
      : null
  }

  return (
    <>
      {item.system.type === 'theme_page' ? (
        <CtaBanner
          mobileSrc={(item.elements as ThemePage).cta_banner__mobile_image?.value[0]?.url}
          desktopSrc={(item.elements as ThemePage).cta_banner__desktop_image?.value[0]?.url}
          buttonText={(item.elements as ThemePage).cta_banner__button_text?.value}
          buttonHref={(item.elements as ThemePage).cta_banner__button_url?.value}
          alt={(item.elements as ThemePage).cta_banner__desktop_image?.value[0]?.description}
        ></CtaBanner>
      ) : (
        <OverviewHeader
          title={(item.elements as CategoryPage).category_brand_header__title?.value}
          text={(item.elements as CategoryPage).category_brand_header__description?.value}
          image={(item.elements as CategoryPage).category_brand_header__image?.value[0]?.url}
          preferenceNumber={dafPreferenceNumber}
          alt={(item.elements as CategoryPage).category_brand_header__image?.value[0]?.description}
          isPreOwned={isPreOwned}
          onClickMakeAppointment={() => initializeContactForm('appointment')}
          breadcrumbItems={breadcrumbs}
        />
      )}
      {hasSubcategories && <CategoryButtons items={subcategories} />}
      <ProductGrid
        promotionLabel={promotionLabels[theme] && promotionLabels[theme]}
        codename={item.system.codename}
        type={item.system.type}
        initialPage={initialPage}
        initialSort={initialSort}
        initialFilters={initialFilters}
        fixedFilters={fixedFilters}
        isPreOwned={isPreOwned}
        webID={item.elements.daf_filters__web_id?.value || 'gssnwsph'}
        onlyMemberDiscountProducts={onlyMemberDiscountProducts}
        theme={
          item.elements.daf_filters__theme.value.length
            ? item.elements.daf_filters__theme.value
            : undefined
        }
        callToActions={callToActions}
        fixedBrands={fixedBrands}
        brands={brands}
        fixedCollections={fixedCollections}
        collections={collections}
        articleTypeID={
          item.elements.daf_filters__article_type_id.value.length
            ? parseInt(item.elements.daf_filters__article_type_id.value, 10)
            : undefined
        }
        preProductBanner={preProductBanner}
      ></ProductGrid>
      <AppointmentUSPBlock
        mt="4rem"
        subject={`Vraag vanaf ${item.elements.title.value} pagina`}
        formType={
          item.elements?.daf_filters__web_id?.value === 'gpcpreown' ? 'pre-owned' : 'default'
        }
      />
      {contentSlides && <InfoBlockSlider items={contentSlides} />}
    </>
  )
}
